/**
 * canonicalize() — чистое воспроизведение fix_formatted_text из docs/telegram.md §4.1–4.3.
 * 02-contracts.md §6 с НОРМАТИВНОЙ заменой шага E на resplit (§Хребет данных 3.4).
 *
 * Зачем она вообще: Telegram переписывает присланный текст (табы → пробелы, \r удаляется,
 * trim, нормализация и слияние сущностей), поэтому хэш от «точных байтов» черновика заверял бы
 * форму, которой в канале никогда не будет. Одна функция обслуживает генерацию, UI, подпись,
 * валидатор и публикатор — подписанное совпадает с сохранённым.
 *
 * ЧИСТОТА (проверяется тестом и правилом R11): ни Date, ни Math.random, ни Intl, ни node:*,
 * ни глобального состояния. Этот файл не импортирует time.ts.
 *
 * Порядок: A предусловия → B правка текста → C trim → D перенос сущностей → E resplit →
 *          F предупреждения. Лимиты считает отдельная checkLimits ПО КАНОНИЧЕСКОЙ форме.
 */

import { CanonicalizeError, hasFatal, type Issue, type IssueCode, issue } from '../issues.ts';
import {
  type CanonicalDraft,
  type DraftContent,
  DraftContentSchema,
  isTelegramUrl,
  type MessageEntity,
  type WireMethodOptions,
  wireMethodOf,
} from '../telegram/draft.ts';
import { assertNoOrderTie, isSendableEntityType, sortEntities } from '../telegram/entity.ts';
import { isSplittable, resplitEntities } from '../telegram/resplit.ts';
import { richBlockIssues } from '../telegram/rich.ts';
import {
  asUtf16,
  type CodePoints,
  codePointLen,
  exceeds,
  isCodeUnitBoundary,
  isWellFormedText,
  type Limit,
  limitValue,
  REQUEST_MAX_UTF8_BYTES,
  RICH_MAX_CODEPOINTS,
  SEND_MESSAGE_MAX_CODEPOINTS,
  utf8Len,
  utf16Len,
} from '../units.ts';
import { CANON_ALGO_VERSION } from '../version.ts';

export { CanonicalizeError };

const QUOTE_CLASS: ReadonlySet<string> = new Set(['blockquote', 'expandable_blockquote']);
const CONTINUOUS_CLASS: ReadonlySet<string> = new Set(['code', 'pre', 'text_link']);

const PRE_LANGUAGE_RE = /^[A-Za-z0-9+#._-]{1,32}$/;

/** Голая ссылка ищется по схеме, а не детектором превью: §Хребет данных 6 запрещает
 *  импорт detect/ из основного входа, и для предупреждения хватает схемы.
 *
 *  Перед схемой — отрицательная ретроспекция, а не список разделителей: детектор сервера
 *  показывает карточку превью и для адреса, приклеенного к предыдущему символу
 *  (`Источник:https://…`), а список разделителей такой адрес не находил вовсе — M-006. */
const BARE_URL_RE = /(?<![A-Za-z0-9+.-])(https?:\/\/[^\s)\]>»"']{3,})/gi;

/**
 * Потолок длины по методу. У `sendMessage` он равен ПОРОГУ МАРШРУТИЗАЦИИ 4064, а не
 * телеграмным 4096, и это сделано намеренно: тогда правило «длиннее 4064 — уходит rich»
 * становится проверяемым на заморозке, без отдельного кода отказа. Черновик в 4070
 * знаков, собранный руками как `sendMessage`, отбивается `text_too_long` и вынужден
 * поехать rich-сообщением.
 */
export function lengthLimitFor(c: Pick<DraftContent, 'method'>): Limit<CodePoints> {
  return c.method === 'sendRichMessage' ? RICH_MAX_CODEPOINTS : SEND_MESSAGE_MAX_CODEPOINTS;
}

// ---------------------------------------------------------------------------
// Шаг A — предусловия. Все fatal.
// ---------------------------------------------------------------------------

export function checkDraftContent(c: DraftContent): Issue[] {
  const out: Issue[] = [];
  const len = utf16Len(c.text) as unknown as number;

  // A1
  if (!isWellFormedText(c.text)) {
    out.push(
      issue('text_not_well_formed', 'Текст содержит одиночный суррогат UTF-16.', {
        path: 'text',
      }),
    );
  }

  c.entities.forEach((e, i) => {
    const path = `entities[${i}]`;
    // A2
    if (e.length <= 0) {
      out.push(issue('entity_zero_length', 'Сущность нулевой длины.', { path }));
    }
    if (e.offset < 0 || e.offset + e.length > len) {
      out.push(
        issue('entity_out_of_range', 'Сущность выходит за границы текста.', {
          path,
          detail: { offset: e.offset, length: e.length, text_utf16: len },
        }),
      );
      return; // дальше по этой сущности проверять нечего
    }
    // A3
    if (!isCodeUnitBoundary(c.text, e.offset) || !isCodeUnitBoundary(c.text, e.offset + e.length)) {
      out.push(
        issue(
          'entity_boundary_in_surrogate_pair',
          'Граница сущности попала в середину суррогатной пары — Telegram ответит 400.',
          { path, detail: { offset: e.offset, length: e.length } },
        ),
      );
    }
    // A4
    if (!isSendableEntityType(e.type)) {
      out.push(
        issue('entity_type_not_sendable', `Тип сущности ${e.type} нельзя отправить явно.`, {
          path,
        }),
      );
    }
    // A5
    if (e.type === 'text_link' && !isTelegramUrl(e.url)) {
      out.push(
        issue('text_link_url_invalid', 'Адрес ссылки должен начинаться с https://.', {
          path: `${path}.url`,
        }),
      );
    }
    // A6
    if (e.type === 'pre' && e.language !== undefined && !PRE_LANGUAGE_RE.test(e.language)) {
      out.push(
        issue('pre_language_invalid', 'Недопустимое имя языка у блока кода.', {
          path: `${path}.language`,
        }),
      );
    }
  });

  // A7
  const lpo = c.link_preview_options;
  if (lpo !== null) {
    if (lpo.prefer_small_media && lpo.prefer_large_media) {
      out.push(
        issue(
          'link_preview_options_conflict',
          'prefer_small_media и prefer_large_media заданы одновременно.',
          { path: 'link_preview_options' },
        ),
      );
    }
    if (lpo.url === null && (lpo.prefer_small_media || lpo.prefer_large_media)) {
      out.push(
        issue(
          'link_preview_options_conflict',
          'prefer_* задан без url — Telegram молча проигнорирует, и превью соврёт.',
          { path: 'link_preview_options' },
        ),
      );
    }
  }

  // A8
  if (c.media !== null && !/^[0-9a-f]{64}$/.test(c.media.sha256)) {
    out.push(issue('media_invalid', 'sha256 медиа не в форме 64 hex.', { path: 'media.sha256' }));
  }

  return out;
}

// ---------------------------------------------------------------------------
// Шаги B–F.
// ---------------------------------------------------------------------------

interface TextRewrite {
  text: string;
  map: Int32Array;
  start: number;
  removedCr: number;
  replacedControls: number;
  trimmedLeading: number;
  trimmedTrailing: number;
}

/**
 * Шаг B — правка текста с картой переиндексации, затем шаг C — trim.
 * Карта обязательна: арифметика по сдвигам ошибается на любом сочетании удалений и замен.
 * Итерируем ТОЛЬКО по code units: for..of здесь был бы ошибкой — он ходит по кодпоинтам.
 */
function rewriteText(src: string): TextRewrite {
  let out = '';
  const map = new Int32Array(src.length + 1);
  let removedCr = 0;
  let replacedControls = 0;

  for (let i = 0; i < src.length; i++) {
    map[i] = out.length;
    const ch = src.charCodeAt(i);
    if (ch === 0x0d) {
      removedCr++;
      continue;
    }
    if (ch === 0x0a) {
      out += '\n';
      continue;
    }
    if (ch <= 0x1f) {
      out += ' ';
      replacedControls++;
      continue;
    }
    if (ch >= 0x2028 && ch <= 0x202e) {
      replacedControls++;
      continue;
    }
    if (ch === 0x030a || ch === 0x0333 || ch === 0x033f) {
      replacedControls++;
      continue;
    }
    out += src[i] as string;
  }
  map[src.length] = out.length;

  // Шаг C — trim ПОСЛЕ замен, чтобы срезался и бывший TAB. Только ' ' и '\n'.
  let start = 0;
  let end = out.length;
  while (end > start && (out[end - 1] === ' ' || out[end - 1] === '\n')) end--;
  while (start < end && (out[start] === ' ' || out[start] === '\n')) start++;

  return {
    text: out.slice(start, end),
    map,
    start,
    removedCr,
    replacedControls,
    trimmedLeading: start,
    trimmedTrailing: out.length - end,
  };
}

const clamp = (n: number, lo: number, hi: number): number => (n < lo ? lo : n > hi ? hi : n);

/** Шаг D — перенос сущностей по карте. */
function remapEntities(
  entities: readonly MessageEntity[],
  rw: TextRewrite,
  warnings: Issue[],
): MessageEntity[] {
  const out: MessageEntity[] = [];
  const limit = rw.text.length;
  for (const e of entities) {
    const o = clamp((rw.map[e.offset] as number) - rw.start, 0, limit);
    const t = clamp((rw.map[e.offset + e.length] as number) - rw.start, 0, limit);
    if (t - o <= 0) {
      warnings.push(
        issue('entity_dropped_by_trim', `Сущность ${e.type} исчезла после правки текста.`, {
          detail: { type: e.type, offset: e.offset, length: e.length },
        }),
      );
      continue;
    }
    out.push({ ...e, offset: asUtf16(o), length: asUtf16(t - o) } as MessageEntity);
  }
  return out;
}

const endOf = (e: { offset: number; length: number }): number => e.offset + e.length;
const overlaps = (a: MessageEntity, b: MessageEntity): boolean =>
  a.offset < endOf(b) && b.offset < endOf(a);
const contains = (outer: MessageEntity, inner: MessageEntity): boolean =>
  outer.offset <= inner.offset && endOf(outer) >= endOf(inner);

/**
 * Шаг E — §Хребет данных 3.4. Вариант «слитые максимальные интервалы на тип»
 * из 02-contracts.md §6.6 отвергнут и здесь не воспроизводится.
 */
function normalizeEntities(entities: readonly MessageEntity[], fatals: Issue[]): MessageEntity[] {
  const quotes = sortEntities(entities.filter((e) => QUOTE_CLASS.has(e.type)));
  const continuous = sortEntities(entities.filter((e) => CONTINUOUS_CLASS.has(e.type)));
  const splittable = entities.filter((e) => isSplittable(e.type));

  // E2 — цитаты не пересекаются и не вкладываются друг в друга.
  for (let i = 0; i < quotes.length; i++) {
    for (let j = i + 1; j < quotes.length; j++) {
      const a = quotes[i] as MessageEntity;
      const b = quotes[j] as MessageEntity;
      if (overlaps(a, b)) {
        fatals.push(
          issue('blockquote_overlap', 'Две цитаты пересекаются или вложены друг в друга.', {
            detail: {
              a: `${a.type} ${a.offset}+${a.length}`,
              b: `${b.type} ${b.offset}+${b.length}`,
            },
          }),
        );
      }
    }
  }

  // E3 — continuous не пересекаются между собой.
  for (let i = 0; i < continuous.length; i++) {
    for (let j = i + 1; j < continuous.length; j++) {
      const a = continuous[i] as MessageEntity;
      const b = continuous[j] as MessageEntity;
      if (overlaps(a, b)) {
        fatals.push(
          issue('continuous_entity_overlap', 'Неразрывные сущности пересекаются.', {
            detail: {
              a: `${a.type} ${a.offset}+${a.length}`,
              b: `${b.type} ${b.offset}+${b.length}`,
            },
          }),
        );
      }
    }
  }

  // E4 — continuous не пересекает границу цитаты. Splittable — можно: resplit её разрежет,
  // ровно как сервер.
  for (const c of continuous) {
    for (const q of quotes) {
      if (overlaps(c, q) && !contains(q, c)) {
        fatals.push(
          issue(
            'entity_crosses_blockquote',
            'Неразрывная сущность пересекает границу цитаты — Telegram выбросит её молча.',
            {
              detail: {
                entity: `${c.type} ${c.offset}+${c.length}`,
                quote: `${q.offset}+${q.length}`,
              },
            },
          ),
        );
      }
    }
  }

  if (fatals.length > 0) return [];

  // E5
  const { entities: resplit, dropped_in_pre } = resplitEntities([
    ...splittable,
    ...quotes,
    ...continuous,
  ]);

  // E6 — исчезнувшее внутри pre/code превращается в fatal, а не в тихую потерю форматирования.
  for (const d of dropped_in_pre) {
    fatals.push(
      issue('entity_inside_code_or_pre', `Форматирование ${d.type} внутри блока кода пропадёт.`, {
        detail: { type: d.type, offset: d.offset, length: d.length },
      }),
    );
  }

  // E7 — финальная сортировка уже сделана внутри resplitEntities.
  return resplit;
}

export interface CanonicalizeOutcome {
  text: string;
  entities: MessageEntity[];
  issues: Issue[];
}

/** Чистое воспроизведение fix_formatted_text. Не бросает: возвращает issues. */
export function canonicalizeFormattedText(
  text: string,
  entities: readonly MessageEntity[],
): CanonicalizeOutcome {
  const issues: Issue[] = [];
  const rw = rewriteText(text);
  const moved = remapEntities(entities, rw, issues);
  const normalized = normalizeEntities(moved, issues);

  // Шаг F — предупреждения.
  if (rw.text !== text) {
    issues.push(
      issue('text_modified_by_canonicalization', 'Telegram изменит текст при сохранении.', {
        detail: {
          removed_cr: rw.removedCr,
          replaced_controls: rw.replacedControls,
          trimmed_leading: rw.trimmedLeading,
          trimmed_trailing: rw.trimmedTrailing,
        },
      }),
    );
    if (rw.removedCr > 0) {
      issues.push(issue('cr_removed', 'Символы возврата каретки удалены.'));
    }
    if (rw.trimmedLeading > 0) {
      issues.push(issue('trimmed_leading', 'Пробелы и переводы строк в начале срезаны.'));
    }
    if (rw.trimmedTrailing > 0) {
      issues.push(issue('trimmed_trailing', 'Пробелы и переводы строк в конце срезаны.'));
    }
    if (rw.replacedControls > 0) {
      issues.push(issue('control_chars_replaced', 'Управляющие символы заменены или удалены.'));
    }
  }

  const cp = codePointLen(rw.text) as unknown as number;
  const cu = utf16Len(rw.text) as unknown as number;
  if (cp !== cu) {
    issues.push(
      issue('non_bmp_present', 'В тексте есть символы вне BMP: кодпоинты и UTF-16 расходятся.', {
        detail: { codepoints: cp, utf16: cu },
      }),
    );
  }

  if (hasBareUrl(rw.text, normalized)) {
    issues.push(
      issue('bare_url_in_text', 'Голая ссылка в тексте: Telegram покажет карточку превью.'),
    );
  }

  if (normalized.length > 100) {
    issues.push(
      issue('entity_count_high', 'Очень много сущностей форматирования.', {
        detail: { count: normalized.length },
      }),
    );
  }

  return { text: rw.text, entities: normalized, issues };
}

/**
 * Голая ссылка = схема http(s):// в тексте, не покрытая ни одной text_link.
 *
 * Перебираются ВСЕ совпадения, а не первое: первый адрес мог быть спрятан в `text_link`,
 * а второй — остаться голым, и предупреждать надо именно про второй (M-006).
 */
function hasBareUrl(text: string, entities: readonly MessageEntity[]): boolean {
  for (const m of text.matchAll(BARE_URL_RE)) {
    const at = m.index + m[0].length - (m[1] as string).length;
    const covered = entities.some((e) => e.type === 'text_link' && e.offset <= at && endOf(e) > at);
    if (!covered) return true;
  }
  return false;
}

/**
 * Предусловие: checkDraftContent(c) не вернул fatal. Иначе бросает CanonicalizeError.
 *
 * Единственное место в репозитории, где разрешён `as CanonicalDraft` — правило R12
 * стража границ отбивает его во всех остальных файлах. Без R12 бренд — украшение.
 */
export function canonicalize(c: DraftContent): CanonicalDraft {
  const pre = checkDraftContent(c);
  if (hasFatal(pre)) {
    throw new CanonicalizeError(
      (pre.find((i) => i.severity === 'fatal') as Issue).code,
      'canonicalize() called on content that failed its preconditions',
      pre,
    );
  }

  const { text, entities, issues } = canonicalizeFormattedText(c.text, c.entities);
  if (hasFatal(issues)) {
    throw new CanonicalizeError(
      (issues.find((i) => i.severity === 'fatal') as Issue).code,
      'content cannot be canonicalized',
      issues,
    );
  }

  assertNoOrderTie(entities);

  return {
    cv: CANON_ALGO_VERSION,
    method: c.method,
    text,
    entities,
    link_preview_options: c.link_preview_options,
    media: c.media,
    // Отсутствующее поле — то же, что `null` (содержание, собранное до 19.09.2026).
    document: c.document ?? null,
  } as CanonicalDraft;
}

/** Лимиты считаются ПО КАНОНИЧЕСКОЙ форме — Telegram хранит и меряет именно её. */
export function checkLimits(c: CanonicalDraft): Issue[] {
  const out: Issue[] = [];
  const limit = lengthLimitFor(c);
  if (exceeds(limit, codePointLen(c.text))) {
    // Код один на оба метода: подписи больше не существует, а число берётся из самого
    // лимита — вписывать 1024/4096 литералом значило бы соврать для rich.
    out.push(
      issue('text_too_long', 'Превышен лимит длины Telegram.', {
        detail: {
          codepoints: codePointLen(c.text) as unknown as number,
          limit: limitValue(limit),
        },
      }),
    );
  }
  // Прежний ПРЕДОХРАНИТЕЛЬ «rich объявлен без причины» (`method_not_derived`) снят 19.09.2026:
  // с политикой бренда «все посты — расширенным сообщением» короткий пост без картинки законно
  // уходит rich (docs/release-2026-09-19.md §9.3). Метод выбирается при заморозке и дальше
  // читается из подписанного — публикатор и сверка не выводят его заново, поэтому форма
  // «короткий rich» больше не значит «карточка на усмотрение Telegram».
  //
  // Взамен — проверка формы, которую rich действительно не выразит: цитата или блок кода
  // обязаны занимать целые строки (`richBlockIssues`). Иначе Telegram отбил бы пост 400 уже
  // после одобрения.
  if (c.method === 'sendRichMessage') {
    for (const p of richBlockIssues(c.text, c.entities)) {
      out.push(
        issue('rich_block_misaligned', p.message, {
          path: 'entities',
          detail: { type: p.type, offset: p.offset, length: p.length, kind: p.kind },
        }),
      );
    }
  }
  if (exceeds(REQUEST_MAX_UTF8_BYTES, utf8Len(c.text))) {
    out.push(
      issue('text_too_long_bytes', 'Тело запроса превышает 32768 байт UTF-8.', {
        detail: { bytes: utf8Len(c.text) as unknown as number },
      }),
    );
  }
  // ЕДИНСТВЕННОЕ, что запрещает пост из одной картинки без текста. Telegram такой
  // rich-пост принял бы (блок photo без единого абзаца законен), и если продукт когда-нибудь
  // его захочет — ослаблять надо ровно эту строку и ничего больше.
  if (c.text.length === 0) {
    out.push(issue('text_empty_after_canonicalization', 'После канонизации текст пуст.'));
  }
  return out;
}

export type FreezeResult =
  | { ok: true; canonical: CanonicalDraft; warnings: Issue[] }
  | { ok: false; issues: Issue[] };

/**
 * Авторские части черновика: всё, что человек или модель ДЕЙСТВИТЕЛЬНО выбирают.
 * `method` сюда не входит — он выводится, и это единственный способ его получить.
 *
 * `entities` и `link_preview_options` объявлены `unknown` НАМЕРЕННО, как и у `freezeDraft`:
 * их форму проверяет `DraftContentSchema`, и вторая проверка в TypeScript означала бы вторую
 * норму. Вызывающий — приём на бэкенде — получает их из недоверенного тела запроса.
 */
export interface AuthoredDraft {
  readonly text: string;
  readonly entities: unknown;
  /**
   * Решение автора о карточке превью. У поста БЕЗ картинки задаётся всегда и явно (§5.1):
   * «карточки не будет» — это `is_disabled: true`, а не `null`. У поста с картинкой — `null`.
   * У поста, переросшего порог, карточка будет СНЯТА, и об этом придёт предупреждение.
   */
  readonly link_preview_options: unknown;
  readonly media: DraftContent['media'];
  /** PDF внизу поста (§9.2); не задан — документа нет. Документ делает пост rich. */
  readonly document?: DraftContent['document'] | undefined;
}

/** Политика бренда в момент заморозки — пока одна: «все посты — расширенным сообщением». */
export type FreezeOptions = WireMethodOptions;

/**
 * ЕДИНСТВЕННЫЙ способ собрать черновик из авторских частей. Лекарство из плана §4
 * (`docs/phase-2/09-rich-transport.md`): производители перестают ПЕРЕДАВАТЬ метод и
 * начинают его ПОЛУЧАТЬ. Зовут её сборка черновика писателем, приём на бэкенде и правка
 * человеком — до неё каждое из трёх мест выводило метод по-своему, а плата за расхождение —
 * `publish_deviation` на КОРРЕКТНО опубликованном посте.
 *
 * ПОЧЕМУ ТЕКСТ КАНОНИЗИРУЕТСЯ ДВАЖДЫ. Порог 4064 считается по КАНОНИЧЕСКОЙ форме, а на входе
 * форма сырая. Первый проход отвечает ровно на один вопрос — какова длина ПОСЛЕ канонизации,
 * — и только потом выбирается метод. Ровно тот случай из §4: черновик в 4070 знаков с
 * десятью хвостовыми пробелами — это 4060 после канонизации, то есть обычное сообщение
 * С КАРТОЧКОЙ, и решение по сырой длине отняло бы её молча.
 *
 * ЧТО ПРОИСХОДИТ С КАРТОЧКОЙ у поста, который всё-таки уехал rich: она СНИМАЕТСЯ, и об этом
 * выдаётся предупреждение `link_preview_dropped_for_rich`. Отбивать такой черновик нельзя —
 * автор не сделал ничего запрещённого; снимать молча тоже нельзя — человек утверждает форму.
 * Предупреждение выдаётся, только если карточка была НУЖНА (`is_disabled: false`): «карточки
 * не будет» у rich-поста — не новость, и с политикой бренда такое предупреждение висело бы на
 * каждом посте, приучая его не читать.
 *
 * ПОЛИТИКА БРЕНДА (`opts.richAlways`, §9.3) — выбор метода, а не вывод: при ней короткий пост
 * без картинки тоже уходит rich. Документ (§9.2) делает пост rich при любой политике.
 */
export function freezeAuthored(a: AuthoredDraft, opts: FreezeOptions = {}): FreezeResult {
  // Проба считает ТОЛЬКО длину, поэтому сущности ей не нужны: канонический текст от них не
  // зависит вовсе (`rewriteText` работает по строке, сущности лишь переносятся следом).
  // Это и позволяет спросить длину до того, как схема проверила `entities`, — а спросить
  // надо именно до, иначе метод пришлось бы угадывать по сырой длине и ошибаться на
  // черновике с хвостовыми пробелами.
  //
  // На битом тексте (одиночный суррогат) проба бессмысленна: пусть отказ придёт из
  // `freezeDraft` целым и с правильным кодом, а не отсюда обрывком.
  let probe = a.text;
  try {
    probe = canonicalizeFormattedText(a.text, []).text;
  } catch {
    probe = a.text;
  }
  const document = a.document ?? null;
  const method = wireMethodOf({ text: probe, media: a.media, document }, opts);

  const frozen = freezeDraft({
    cv: CANON_ALGO_VERSION,
    method,
    text: a.text,
    entities: a.entities,
    // Параметра `link_preview_options` у sendRichMessage не существует вовсе.
    link_preview_options: method === 'sendMessage' ? a.link_preview_options : null,
    media: a.media,
    document,
  });

  if (!frozen.ok || method === 'sendMessage' || !cardWanted(a.link_preview_options)) return frozen;
  return {
    ...frozen,
    warnings: [
      ...frozen.warnings,
      issue('link_preview_dropped_for_rich', droppedCardText(a, document, probe), {
        path: 'link_preview_options',
      }),
    ],
  };
}

/** Автор хотел карточку: опции заданы и карточка не выключена. Форму проверит схема. */
function cardWanted(lpo: unknown): boolean {
  return (
    typeof lpo === 'object' &&
    lpo !== null &&
    (lpo as { readonly is_disabled?: unknown }).is_disabled !== true
  );
}

/** Почему карточки не будет — причина по первой сработавшей, в порядке выбора метода. */
function droppedCardText(
  a: AuthoredDraft,
  document: DraftContent['document'],
  probe: string,
): string {
  if (a.media !== null) return 'Карточка превью снята: у поста с картинкой её не бывает.';
  if (document !== null) return 'Карточка превью снята: у поста с файлом её не бывает.';
  if (wireMethodOf({ text: probe, media: null }) === 'sendRichMessage') {
    return 'Карточка превью снята: пост длиннее порога и уходит одним rich-сообщением.';
  }
  return 'Карточка превью снята: все посты бренда уходят расширенным сообщением (Настройки).';
}

/**
 * Отображение отказа схемы в НАСТОЯЩИЙ код проблемы.
 *
 * Раньше здесь стоял один `media_invalid` на все случаи, и это было видно человеку:
 * на текстовом посте с битой ссылкой экран одобрения показывал «media_invalid», а коды
 * entity_type_not_sendable и text_link_url_invalid не могли возникнуть вовсе — схема
 * отбивает такой вход раньше предусловий A4 и A5, и те ветки были мёртвыми.
 *
 * Путь issue'а от zod несёт всё нужное: `entities.3.type`, `entities.3.url`, `media.sha256`.
 */
/** Значение по пути issue'а из исходного объекта: сообщение zod его не несёт. */
function valueAtPath(raw: unknown, path: readonly PropertyKey[]): unknown {
  let cur: unknown = raw;
  for (const k of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<PropertyKey, unknown>)[k];
  }
  return cur;
}

/** Типы, запрещённые именно в MVP, а не «неотправляемые вообще». */
const FORBIDDEN_IN_MVP: ReadonlySet<string> = new Set(['custom_emoji', 'text_mention']);

function codeForSchemaIssue(
  raw: unknown,
  path: readonly PropertyKey[],
  message: string,
): IssueCode {
  const parts = path.map(String);
  const head = parts[0];
  const tail = parts[parts.length - 1];

  // Инварианты из .check() приходят своим сообщением, а не путём.
  if (message === 'method_media_mismatch' || message === 'lpo_method_mismatch') {
    return 'link_preview_options_conflict';
  }
  if (message === 'method_document_mismatch') return 'document_invalid';

  if (head === 'entities') {
    if (tail === 'url') return 'text_link_url_invalid';
    if (tail === 'language') return 'pre_language_invalid';
    // Дискриминированный union не принял тип. Сообщение zod у всех отвергнутых типов
    // ОДИНАКОВОЕ и самого типа не содержит — читаем его из исходного объекта.
    if (tail === 'type' || parts.length <= 2) {
      const actual = valueAtPath(raw, tail === 'type' ? path : [...path, 'type']);
      return typeof actual === 'string' && FORBIDDEN_IN_MVP.has(actual)
        ? 'entity_type_forbidden_mvp'
        : 'entity_type_not_sendable';
    }
    if (tail === 'offset' || tail === 'length') return 'entity_out_of_range';
    return 'entity_type_not_sendable';
  }

  if (head === 'media') return 'media_invalid';
  if (head === 'document') return 'document_invalid';
  if (head === 'link_preview_options') return 'link_preview_options_conflict';
  if (head === 'cv') return 'canon_algo_mismatch';
  if (head === 'text') return 'text_not_well_formed';

  return 'unrecognized_field';
}

/**
 * Заморозка ГОТОВОГО контента: `method` уже проставлен и здесь ПРОВЕРЯЕТСЯ, а не выводится.
 * Так её зовут подписант и предотправочный валидатор — им приезжает то, что человек уже
 * одобрил, и вывести метод заново значило бы согласиться с чужим расхождением вместо того,
 * чтобы его заметить.
 *
 * Собирающим черновик из авторских частей нужна не она, а `freezeAuthored` выше.
 *
 * При любом fatal возвращает ВСЕ собранные issue, а не первый.
 */
export function freezeDraft(raw: unknown): FreezeResult {
  const parsed = DraftContentSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((i) =>
        issue(codeForSchemaIssue(raw, i.path, i.message), i.message, { path: i.path.join('.') }),
      ),
    };
  }

  const pre = checkDraftContent(parsed.data);
  if (hasFatal(pre)) return { ok: false, issues: pre };

  const outcome = canonicalizeFormattedText(parsed.data.text, parsed.data.entities);
  if (hasFatal(outcome.issues)) return { ok: false, issues: [...pre, ...outcome.issues] };

  const canonical = canonicalize(parsed.data);
  const limits = checkLimits(canonical);
  if (hasFatal(limits)) return { ok: false, issues: [...pre, ...outcome.issues, ...limits] };

  return { ok: true, canonical, warnings: [...pre, ...outcome.issues, ...limits] };
}
